import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { ProjectNote } from '../../../types/projects';

interface ProjectNoteModalProps {
    show: boolean;
    onHide: () => void;
    onSave: (title: string, content: string) => Promise<void>;
    existingNote?: ProjectNote | null;
    submitting?: boolean;
}

export const ProjectNoteModal: React.FC<ProjectNoteModalProps> = ({
    show,
    onHide,
    onSave,
    existingNote,
    submitting = false,
}) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');

    const isEditing = !!existingNote;

    // Reset form when modal opens/closes or when existingNote changes
    useEffect(() => {
        if (show) {
            if (existingNote) {
                // Handle notes with missing title/content (e.g., from guest API)
                setTitle(existingNote.title || '');
                setContent(existingNote.content || '');
            } else {
                setTitle('');
                setContent('');
            }
        }
    }, [show, existingNote]);

    const handleSave = async () => {
        if (!title.trim() || !content.trim()) return;

        try {
            await onSave(title.trim(), content.trim());
            onHide();
        } catch (err) {
            // Error handling done in parent
            console.error('Error saving note:', err);
        }
    };

    const handleClose = () => {
        setTitle('');
        setContent('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {isEditing ? 'Redigera anteckning' : 'Ny anteckning'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Titel</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Anteckningstitel"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            disabled={submitting}
                            autoFocus
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Innehåll</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            placeholder="Anteckningsinnehåll"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            disabled={submitting}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={submitting || !title.trim() || !content.trim()}
                >
                    {isEditing ? 'Spara' : 'Lägg till'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
